/**
 * Speaking waveform visualization component.
 *
 * Displays a row of bars during recording / audio playback. When a `getLevels`
 * callback is provided (backed by useAudioAnalyser), the bars reflect the REAL
 * audio amplitude — flat when the mic/speaker is silent. When `getLevels` is
 * omitted, it falls back to a generic animated waveform.
 *
 * @module SpeakingWaveform
 */

import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

const BAR_COUNT = 32;
const MIN_HEIGHT = 12; // % — baseline so silent bars are still visible

interface SpeakingWaveformProps {
  readonly isRecording: boolean;
  readonly className?: string;
  readonly barColor?: string; // Optional custom color for the bars
  /**
   * Optional real-audio driver. Fills the provided array with 0–1 magnitudes and
   * returns true if real data is available. When provided, fake animation is
   * disabled and bars show actual audio (flat on silence).
   */
  readonly getLevels?: (out: number[]) => boolean;
}

export function SpeakingWaveform({ isRecording, className, barColor, getLevels }: SpeakingWaveformProps) {
  const barsRef = useRef<HTMLDivElement[]>([]);
  const animationRef = useRef<number>();
  // Reusable buffer so we don't allocate every frame
  const levelsRef = useRef<number[]>(new Array(BAR_COUNT).fill(0));

  useEffect(() => {
    if (isRecording) {
      const animate = () => {
        const out = levelsRef.current;
        const hasReal = getLevels ? getLevels(out) : false;

        barsRef.current.forEach((bar, index) => {
          if (!bar) return;
          let height: number;
          if (getLevels) {
            // Real-audio mode: use actual amplitude (flat baseline on silence)
            height = hasReal
              ? MIN_HEIGHT + Math.min(1, out[index] ?? 0) * (100 - MIN_HEIGHT)
              : MIN_HEIGHT;
          } else {
            // Fallback generic animation
            height = 30 + Math.sin(Date.now() / 150 + index * 0.4) * 25 + Math.random() * 20;
          }
          bar.style.height = `${height}%`;
        });
        animationRef.current = requestAnimationFrame(animate);
      };
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      barsRef.current.forEach((bar) => {
        if (bar) bar.style.height = '15%';
      });
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isRecording, getLevels]);

  return (
    <div className={cn("flex items-center justify-center gap-1 sm:gap-1.5 h-16 sm:h-20", className)}>
      {Array.from({ length: BAR_COUNT }).map((_, i) => (
        <div
          key={`bar-${i}`}
          ref={(el) => {
            if (el) barsRef.current[i] = el;
          }}
          className={cn(
            "w-1 sm:w-2 rounded-full transition-all duration-75",
            i >= 16 && "hidden sm:block", // Show exactly 16 bars on mobile, 32 total on desktop
            isRecording
              ? (barColor || "bg-exam-recording")
              : "bg-muted-foreground/20"
          )}
          style={{ height: '15%' }}
        />
      ))}
    </div>
  );
}
